import axios from 'axios';

/**
 * API endpoint for map search
 *
 * @param {string} searchTerm - search value
 */
const SEARCH_API = '/api/search';

/**
 * Query the geonames search API and return an array of results
 *
 * @param {string} searchTerm - search input: geonames keywords
 * @returns {Array} - search results objects
 */
export function getGeonamesSearch(searchTerm) {
    return axios.get(SEARCH_API, {
        params: {
            searchTerm,
        },
    })
        .then((response) => response.data);
}

/**
 * Query the WFI search API and return an array of results
 *
 * @param {string} wfi - watershed feature ID
 * @returns {Array} - search results objects
 */
export function getWFISearch(wfi) {
    return axios.get(`${SEARCH_API}/wfi`, {
        params: {
            wfi,
        },
    })
        .then((response) => response.data);
}

// watershed reporting

const WATERSHED_API = '/api/watershed';
const LICENCE_API = '/api/licence';

/**
 * Use the zoom level to calculate the search distance that we should query with in
 *
 * @param {number} zoomLevel - the zoom level of the map
 * @returns {number} - the search distance in meters
 */
function getSearchDistance(zoomLevel) {
    if (zoomLevel) {
        if (zoomLevel > 13) {
            // regardless how much further we're zoomed in, we won't have a minimum search distance of less than 100m
            return 100;
        }
        if (zoomLevel > 7) {
            // why 23?
            //      -> 13 - 7 = 6
            //      -> 6 ** 3 = 216
            //      -> (5000 - 100) / 216 ~= 22.6852
            // we add 100 to the value in case zoomLevel is close to 13 (100m is the minimum search distance)
            return (23 * ((13 - zoomLevel) ** 3)) + 100;
        }
    }

    // override the searchDistance - this is a special case when the WFI is searched for instead
    // of a click on the map.
    if (zoomLevel === 0) {
        return 0;
    }

    // if zoomLevel <= 7 return default value of 5000m
    return 5000;
}

/**
 * Get watershed data by query coordinates as well as the watershed GeoJSON Polygon shape
 *
 * @param {mapboxgl.LngLat} lngLat - LngLat object with query coordinates
 * @param {number} zoomLevel - the zoom level to base the search on
 * @returns {Promise} - promise resolving to waterhed metadata object
 */
export function getWatershedByCoords(lngLat, zoomLevel) {
    const { lng, lat } = lngLat;

    const searchDistance = getSearchDistance(zoomLevel);

    return axios.get(`${WATERSHED_API}`, {
        params: {
            lng,
            lat,
            searchDistance,
        },
    })
        .then((response) => response.data);
}

/**
 * Get watershed report information given lat/lng coordinates and a WFI
 *
 * @param {mapboxgl.LngLat} lngLat - LngLat object with query coordinates
 * @param {string} wfi - WFI corresponding to a watershed
 * @param {string} watershedName - display name for the watershed
 * @param {string} fwa - FWA code for the watershed
 * @returns {Promise} - promise resolving to the watershed report object
 */
export function getWatershedReport(lngLat, wfi, watershedName, fwa) {
    const { lng, lat } = lngLat;
    return axios.get(`${WATERSHED_API}/${wfi}/report`, {
        params: {
            lng,
            lat,
            watershedName,
            fwa,
        },
    })
        .then((response) => response.data);
}

/**
 * Get watershed report PDF file
 *
 * @param {mapboxgl.LngLat} lngLat - LngLat object with query coordinates
 * @param {string} wfi - WFI corresponding to a watershed
 * @param {string} watershedName - display name for the watershed
 * @param {string} fwa - FWA code for the watershed
 * @returns {Promise} - promise resolving to the watershed report PDF file
 */
export function getWatershedReportPdf(lngLat, wfi, watershedName, fwa) {
    const { lng, lat } = lngLat;
    return axios.get(`${WATERSHED_API}/${wfi}/report/pdf`, {
        params: {
            lng,
            lat,
            watershedName,
            fwa,
        },
        responseType: 'arraybuffer',
    })
        .then((response) => response.data);
}

/**
 * Get licence details by "fs_id"
 *
 * @param {id} id - "Foundry Spatial ID" field from the db
 * @returns {Promise} - promise resolving to an object of licence details
 */
export function getLicenceDetails(id) {
    return axios.get(`${LICENCE_API}/${id}`, {})
        .then((response) => response.data);
}

// ECA reporting

const ECA_API = '/api/eca';

/**
 * Get watershed data by query coordinates as well as the watershed GeoJSON Polygon shape
 *
 * @param {mapboxgl.LngLat} lngLat - LngLat object with query coordinates
 * @param {number} zoomLevel - the zoom level to base the search on
 * @returns {Promise} - promise resolving to waterhed metadata object
 */
export function getEcaWatershedByCoords(lngLat, zoomLevel) {
    const { lng, lat } = lngLat;

    const searchDistance = getSearchDistance(zoomLevel);

    return axios.get(`${ECA_API}/watershed`, {
        params: {
            lng,
            lat,
            searchDistance,
        },
    })
        .then((response) => response.data);
}

/**
 * Get the ECA watershed rollup information
 *
 * @param {number} wfi The watershed ID
 * @returns {Promise} - A promise resolving to an array of objects corresponding to data by year
 */
export function getEcaRollup(wfi) {
    return axios.get(`${ECA_API}/${wfi}`, {
    })
        .then((response) => response.data);
}

// export vue plugin
export default {
    install(Vue) {
        // eslint-disable-next-line no-param-reassign
        Vue.prototype.$api = {
            getLicenceDetails,
            getGeonamesSearch,
            getWFISearch,
            getWatershedReport,
            getWatershedReportPdf,
            getWatershedByCoords,
            getEcaWatershedByCoords,
            getEcaRollup,
        };
    },
};
